import React, { useEffect, useState } from 'react';

import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";

import ICoords from '../../interfaces/ICoords';
import ICharger, { IOutlet } from '../../interfaces/ICharger';

import 'leaflet/dist/leaflet.css'
import styles from './styles.module.scss'
import StartIcon from '../../public/assets/icons/start.png'
import DestinationIcon from '../../public/assets/icons/destination.png'
import ChargerOffRouteIcon from '../../public/assets/icons/charger-off-route.png'
import ChargerIcon from '../../public/assets/icons/charger.png'
import FastChargeIcon from '../../public/assets/icons/fast-charger.png'
import ShadowIcon from 'leaflet/dist/images/marker-shadow.png'


// Zoom and Center to show Ireland
const center = {
  lat: 53.3250536,
  lng: -7.75
};
const zoom = 7.2;

interface IMap {
	route?: any;
  charges?: ICharger[];
}

const Map = ({
	route, charges
}: IMap) => {
	const [leafMap, setLeafMap] = useState<L.Map>()
	const [polyline, setPolyline] = useState<L.Polyline>()

  useEffect(() => {
    if (leafMap && polyline) {
      const bounds = polyline.getBounds();
      
      if (Object.keys(bounds).length !== 0) {
        leafMap.fitBounds(bounds, {
          padding: [20, 20]
        });
      }
    }
  }, [leafMap, polyline])

  const getCoords = (step: any) => {
    return {
      lat: step.lat,
      lng: step.long || step.lon,
    }
  }
  const getLatLng = (step: any) => {
    return L.latLng(step.lat, step.lon || step.long)
  }
  const getLatLngArray = (object: any) => {
    return L.latLng(object[0], object[1])
  }
  const isFastCharger = (charger: ICharger) => {
    for (const outlet of charger.outlets) {
      if (outlet.power >= 50) {
        return true  
      }
    }

    return false
  }

  const buildPath = (steps: any) => {
    const paths: L.LatLng[] = []

    for (let step of steps) {
      paths.push(getLatLng(step))

      step.path.forEach((waypoint: any) => {
        paths.push(getLatLngArray(waypoint))
      })
    }

    return paths
  }

	const drawRoute = () => {
    if (route) {
      const paths = buildPath(route.steps);
      
      if (polyline) {
        polyline.setLatLngs(paths);
      }
    }
	}


	useEffect(() => {

		if (leafMap) {
			const polyline = L.polyline([]).addTo(leafMap)
			setPolyline(polyline)
		}

	}, [leafMap])


	const getIcon = (icon: StaticImageData) => {
		return L.icon({
			iconUrl: icon.src,
			shadowUrl: ShadowIcon.src,
			iconSize: [29, 40.5],
			iconAnchor: [14.5, 40.5]
		});
	}

  const renderChargerInfo = (charger: ICharger) => {
    const uniqueOutlets: IOutlet[] = [];

    for (const outlet of charger.outlets) {
      uniqueOutlets.push(outlet);
    }
    uniqueOutlets.sort((a: IOutlet, b: IOutlet) => a.power - b.power);

    return (
      <span>
        Name: {charger.name}<br/>
        Address: {charger.address}<br/>
        Outlets:<br/>
        <ul>
          {uniqueOutlets.map((outlet: IOutlet, index: number) => (
            <li key={index}>{outlet.type} - {outlet.power}kW</li>
          ))}
        </ul>
      </span>
    )
  }


  const renderChargers = () => {
    if (charges && charges.length !== 0) {
      const renderedCharges = [];

      for (const [key, charge] of Object.entries(charges)) {
        const coords = getCoords(charge)
        const info = renderChargerInfo(charge);
        const icon = isFastCharger(charge) ? getIcon(FastChargeIcon) : getIcon(ChargerOffRouteIcon);

        renderedCharges.push(renderMarker(coords, icon, info))
      }

      return renderedCharges
    }
  }
  

  const renderWaypoints = () => {
    if (route) {
      const startCoords = getCoords(route.steps[0])
      const destinationCoords = getCoords(route.steps[route.steps.length - 1])
  
      const renderedCharges = [];

      for (let charger = 1; charger < route.steps.length - 1; ++charger) {
        const routeCharger = route.steps[charger]
        
        const coords = getCoords(routeCharger)
        
        let info: any = routeCharger.name;

        if (charges && charges?.length !== 0) {
          for (const charger of charges) {
            if (charger.name.toLowerCase() === info.toLowerCase()) {
              info = renderChargerInfo(charger);
              break;
            }
          }
        }

        renderedCharges.push({ coords, info })
      }

      return (
        <>
          { renderMarker(startCoords, getIcon(StartIcon), "Start Point", 1000) }
          
          {
            renderedCharges.map(charger => {
              return renderMarker(charger.coords, getIcon(ChargerIcon), charger.info, 1000)
            })
          }

          { renderMarker(destinationCoords, getIcon(DestinationIcon), "Destination Point", 1000) }
        </>
       )
    }
  }

  const renderMarker = (position: ICoords, icon: L.Icon<L.IconOptions>, info?: string | JSX.Element, zIndex = 300) => {
    return (
      <Marker key={`${position.lat + position.lng}`} position={position} icon={icon} zIndexOffset={zIndex}>
        { info && <Popup>{info}</Popup> }
      </Marker>
    )
  }	

  return (
    <MapContainer 
      zoomControl={false} center={center} zoom={zoom}
      className={styles.mapContainer} whenCreated={map => setLeafMap(map)}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      { renderChargers() }
    
      { renderWaypoints() }  

			{ drawRoute() }
			
    </MapContainer>
  )
}

export default Map;